import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBearerToken } from './auth/getApiBearerToken';
import { calcAuth0AuthorizationParams, isAuth0RedirectCallback } from './auth0Params';
import { calcAuth0Audience, calcAuth0RedirectUri } from './calcConfig';
import AppShell from './components/chrome/AppShell';
import PresetPicker from './components/PresetPicker';
import SaveDialog from './components/SaveDialog';
import Toast from './components/Toast';
import Dashboard from './pages/Dashboard';
import Landing from './pages/Landing';
import Methodology from './pages/Methodology';
import CalculatorTerms from './pages/CalculatorTerms';
import ProfileCompletion from './pages/ProfileCompletion';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Wizard from './pages/Wizard';
import { logoSrc, logoStyleChrome, productName } from './brand';
import PageFooter from './components/chrome/PageFooter';
import { configureApi } from './api/client';
import { getProfile } from './api/profile';
import { listPricingCatalog } from './api/pricingCatalog';
import { setPricingCatalog } from './lib/pricingCatalogStore';
import { showToast } from './lib/toast';
import { APP_PATH, defaultAuthedPath, syncAppPath } from './routes';

function isTermsPath(pathname) {
  if (typeof pathname !== 'string') return false;
  return pathname.startsWith(APP_PATH.terms);
}

function pickPublicPage() {
  if (typeof window === 'undefined') return 'landing';
  const p = window.location.pathname;
  if (p.startsWith('/signin')) return 'signin';
  if (p.startsWith('/signup')) return 'signup';
  if (p.startsWith('/methodology')) return 'methodology';
  if (isTermsPath(p)) return 'terms';
  return 'landing';
}

function pushPublicPath(page) {
  if (typeof window === 'undefined') return;
  const target = page === 'landing' ? '/' : `/${page}`;
  if (window.location.pathname !== target) {
    window.history.pushState({}, '', target);
  }
}

export default function App() {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const authRedirectInFlight = useRef(false);
  const audience = calcAuth0Audience();
  const [profileState, setProfileState] = useState('idle');
  const [view, setView] = useState('dashboard');
  const [seed, setSeed] = useState({ inputs: null, name: '', publicId: null, savedAt: null });
  const [wizardKey, setWizardKey] = useState(0);
  const [presetPickerOpen, setPresetPickerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [currentInputs, setCurrentInputs] = useState(null);
  const [pricingCatalogMeta, setPricingCatalogMeta] = useState(null);
  const [accountPrefsOpen, setAccountPrefsOpen] = useState(false);
  const [dashboardTick, setDashboardTick] = useState(0);
  const [incompleteTermsOpen, setIncompleteTermsOpen] = useState(() =>
    typeof window !== 'undefined' && isTermsPath(window.location.pathname),
  );

  const signInWithGoogle = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: calcAuth0AuthorizationParams({ connection: 'google-oauth2' }),
      }),
    [loginWithRedirect],
  );

  const signUpWithGoogle = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: calcAuth0AuthorizationParams({
          connection: 'google-oauth2',
          screen_hint: 'signup',
        }),
      }),
    [loginWithRedirect],
  );

  const signInWithEmail = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: calcAuth0AuthorizationParams({}),
      }),
    [loginWithRedirect],
  );

  const signUpWithEmail = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: calcAuth0AuthorizationParams({ screen_hint: 'signup' }),
      }),
    [loginWithRedirect],
  );

  const handleSessionExpired = useCallback(() => {
    if (authRedirectInFlight.current) return;
    authRedirectInFlight.current = true;
    setProfileState('idle');
    logout({
      logoutParams: { returnTo: calcAuth0RedirectUri() || window.location.origin },
    });
  }, [logout]);

  const configureAuthenticatedApi = useCallback(() => {
    configureApi({
      getToken: () =>
        getApiBearerToken({ getAccessTokenSilently, getIdTokenClaims, audience }),
      onUnauthorized: handleSessionExpired,
      onProfileIncomplete: () => {
        setProfileState('incomplete');
        if (!isTermsPath(window.location.pathname)) {
          syncAppPath(APP_PATH.profileCompletion);
          setIncompleteTermsOpen(false);
        }
      },
    });
  }, [audience, getAccessTokenSilently, getIdTokenClaims, handleSessionExpired]);

  const configureApiRef = useRef(configureAuthenticatedApi);
  configureApiRef.current = configureAuthenticatedApi;

  useEffect(() => {
    if (!isAuthenticated) {
      authRedirectInFlight.current = false;
      setProfileState('idle');
      setPricingCatalogMeta(null);
      setPricingCatalog(null);
      return undefined;
    }

    let cancelled = false;
    setProfileState('loading');
    configureApiRef.current();

    (async () => {
      try {
        const [profile, pricingCatalog] = await Promise.all([
          getProfile(),
          listPricingCatalog().catch(() => null),
        ]);
        if (pricingCatalog) {
          setPricingCatalog(pricingCatalog);
          setPricingCatalogMeta(pricingCatalog);
        }
        if (cancelled) return;
        if (profile.completion_required) {
          setProfileState('incomplete');
          const onTerms = isTermsPath(window.location.pathname);
          setIncompleteTermsOpen(onTerms);
          if (!onTerms) {
            syncAppPath(APP_PATH.profileCompletion);
          }
          return;
        }
        setProfileState('complete');
        const path = window.location.pathname;
        if (path.startsWith(APP_PATH.terms)) {
          setView('terms');
        } else if (
          path === APP_PATH.home
          || path.startsWith('/signin')
          || path.startsWith('/signup')
          || path === APP_PATH.profileCompletion
          || path.startsWith('/valuation')
        ) {
          syncAppPath(defaultAuthedPath());
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401 || err?.code === 'unauthorized') {
          handleSessionExpired();
          return;
        }
        setProfileState('complete');
        showToast('Could not load your profile. Some features may be unavailable.', { tone: 'warn' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, handleSessionExpired]);

  useEffect(() => {
    if (profileState !== 'incomplete') return undefined;
    function onPop() {
      setIncompleteTermsOpen(isTermsPath(window.location.pathname));
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [profileState]);

  if (isLoading || isAuth0RedirectCallback()) {
    return <CenterMsg msg="Checking session…" />;
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedRouter
        onSignIn={signInWithGoogle}
        onSignUp={signUpWithGoogle}
        onSignInWithEmail={signInWithEmail}
        onSignUpWithEmail={signUpWithEmail}
      />
    );
  }

  if (profileState === 'loading' || profileState === 'idle') {
    return <CenterMsg msg="Loading your account…" />;
  }

  if (profileState === 'incomplete') {
    if (incompleteTermsOpen) {
      return (
        <CalculatorTerms
          onBack={() => {
            syncAppPath(APP_PATH.profileCompletion);
            setIncompleteTermsOpen(false);
          }}
        />
      );
    }
    return (
      <ProfileCompletion
        user={user}
        onOpenTerms={() => {
          window.history.pushState({}, document.title, APP_PATH.terms);
          setIncompleteTermsOpen(true);
        }}
        onCompleted={() => {
          setProfileState('complete');
          syncAppPath(defaultAuthedPath());
          setView('dashboard');
        }}
        onSignOut={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      />
    );
  }

  function handleNewValuationCTA() {
    setSeed({ inputs: null, name: '', publicId: null, savedAt: null });
    setWizardKey((k) => k + 1);
    setView('wizard');
    syncAppPath(APP_PATH.wizard);
  }

  function handleOpenPresetPicker() {
    setPresetPickerOpen(true);
  }

  function handlePresetSelected(inputs, name) {
    setSeed({ inputs, name, publicId: null, savedAt: null });
    setWizardKey((k) => k + 1);
    setPresetPickerOpen(false);
    setView('wizard');
    syncAppPath(APP_PATH.wizard);
  }

  function handleOpenSaved({ inputs, name, publicId, updatedAt }) {
    setSeed({ inputs, name, publicId, savedAt: updatedAt ? new Date(updatedAt) : null });
    setWizardKey((k) => k + 1);
    setView('wizard');
    syncAppPath(APP_PATH.wizard);
  }

  function handleSaved(saved) {
    setSeed((prev) => ({
      ...prev,
      name: saved.name,
      publicId: saved.public_id,
      savedAt: saved.updated_at ? new Date(saved.updated_at) : new Date(),
    }));
    setSaveDialogOpen(false);
    showToast(`Saved "${saved.name}".`, { tone: 'success' });
  }

  const navItems = [
    {
      key: 'dashboard',
      label: 'My valuations',
      onClick: () => {
        setView('dashboard');
        syncAppPath(APP_PATH.dashboard);
      },
      active: view === 'dashboard',
    },
    {
      key: 'calc',
      label: 'Calculator',
      onClick: () => {
        setView('wizard');
        syncAppPath(APP_PATH.wizard);
      },
      active: view === 'wizard',
    },
    {
      key: 'methodology',
      label: 'Methodology',
      onClick: () => {
        setView('methodology');
        syncAppPath(APP_PATH.methodology);
      },
      active: view === 'methodology',
    },
  ];

  return (
    <>
      <AppShell
        user={user}
        pricingCatalog={pricingCatalogMeta}
        onSignOut={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        navItems={navItems}
        footerBottomInset={view === 'wizard' ? 72 : 0}
        accountPrefsOpen={accountPrefsOpen}
        onAccountPrefsOpenChange={setAccountPrefsOpen}
        onProfileSettingsSaved={() => setDashboardTick((n) => n + 1)}
        onShowMethodology={() => {
          setView('methodology');
          syncAppPath(APP_PATH.methodology);
        }}
      >
        {view === 'dashboard' ? (
          <Dashboard
            key={dashboardTick}
            user={user}
            onOpen={handleOpenSaved}
            onNew={handleNewValuationCTA}
            onPickPreset={handleOpenPresetPicker}
            onOpenAccountPreferences={() => setAccountPrefsOpen(true)}
          />
        ) : view === 'methodology' ? (
          <Methodology />
        ) : view === 'terms' ? (
          <CalculatorTerms
            onBack={() => {
              setView('dashboard');
              syncAppPath(defaultAuthedPath());
            }}
          />
        ) : (
          <Wizard
            key={wizardKey}
            initialInputs={seed.inputs}
            draftName={seed.name}
            publicId={seed.publicId}
            savedAt={seed.savedAt}
            onInputsChange={setCurrentInputs}
            onSave={() => setSaveDialogOpen(true)}
            onOpenPresetPicker={handleOpenPresetPicker}
            onShowMethodology={() => {
              setView('methodology');
              syncAppPath(APP_PATH.methodology);
            }}
          />
        )}
      </AppShell>

      {presetPickerOpen && (
        <PresetPicker onSelect={handlePresetSelected} onClose={() => setPresetPickerOpen(false)} />
      )}

      {saveDialogOpen && (
        <SaveDialog
          mode={seed.publicId ? 'update' : 'create'}
          initialName={seed.name}
          publicId={seed.publicId}
          inputs={currentInputs || seed.inputs}
          onSaved={handleSaved}
          onClose={() => setSaveDialogOpen(false)}
        />
      )}

      <Toast />
    </>
  );
}

function UnauthenticatedRouter({ onSignIn, onSignUp, onSignInWithEmail, onSignUpWithEmail }) {
  const [page, setPage] = useState(pickPublicPage());

  useEffect(() => {
    function onPop() {
      setPage(pickPublicPage());
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function go(next) {
    pushPublicPath(next);
    setPage(next);
  }

  if (page === 'signin') {
    return <SignIn onSignIn={onSignIn} onSignInWithEmail={onSignInWithEmail} onSignUp={() => go('signup')} onLanding={() => go('landing')} />;
  }
  if (page === 'signup') {
    return <SignUp onContinueWithGoogle={onSignUp} onSignUpWithEmail={onSignUpWithEmail} onSignIn={() => go('signin')} onLanding={() => go('landing')} />;
  }
  if (page === 'methodology') {
    return <PublicMethodology onLanding={() => go('landing')} onSignIn={() => go('signin')} />;
  }
  if (page === 'terms') {
    return (
      <PublicCalculatorTerms onLanding={() => go('landing')} onSignIn={() => go('signin')} />
    );
  }
  return (
    <Landing
      onSignUp={() => go('signup')}
      onSignIn={() => go('signin')}
      onMethodology={() => go('methodology')}
    />
  );
}

function PublicCalculatorTerms({ onLanding, onSignIn }) {
  return (
    <CalculatorTerms
      onBack={onLanding}
      showSignIn={onSignIn}
    />
  );
}

function PublicMethodology({ onLanding, onSignIn }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{
        height: '76px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 56px',
        borderBottom: '1px solid var(--line)',
        gap: '24px',
      }}
      >
        <button type="button" onClick={onLanding} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
          <img src={logoSrc} alt={productName} style={logoStyleChrome} />
        </button>
        <nav style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '22px' }}>
          <button type="button" onClick={onSignIn} style={{ background: 'transparent', border: 'none', color: 'var(--ink2)', fontSize: '13.5px', cursor: 'pointer', fontFamily: 'var(--font)' }}>Sign in</button>
        </nav>
      </header>
      <div style={{ flex: 1 }}>
        <Methodology />
      </div>
      <PageFooter />
    </div>
  );
}

function CenterMsg({ msg, action }) {
  return (
    <div style={centerStyle}>
      <p style={{ color: 'var(--ink2)', fontSize: '15px' }}>{msg}</p>
      {action}
    </div>
  );
}

const centerStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  background: 'var(--bg)',
};
